import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../lib/auth.js';
import { api, ApiError } from '../lib/api.js';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useFormFields } from '../hooks/useFormFields.js';
import { Pagination } from '../components/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import { draftManager } from '../lib/draftManager.js';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave.js';
import { DraftRecoveryBanner } from '../components/DraftRecoveryBanner.js';
import { SectionDraftsView } from '../components/SectionDraftsView.js';
import type { UserDraft } from '@gng-ehs/shared';
import { TableLoader } from '../components/TableLoader';
import { CompanyHoverBadge } from '../components/CompanyHoverBadge';
import { SortableHeader } from '../components/SortableHeader';
import { TimestampCell } from '../components/TimestampCell';
import { formatDateOnly } from '../lib/dateUtils';
import { useBulkSelection } from '../hooks/useBulkSelection';
import {
  generateSmartShortCodeCandidates,
  validateMultipleEmails,
  validateOptionalMultipleEmails,
  formatMultipleEmails,
} from '@gng-ehs/shared';
import { BulkActionBar } from '../components/BulkActionBar';

interface Company {
  id: string;
  short_code: string;
  company_name: string;
  occupier_name: string;
  factory_address: string;
  license_number?: string;
  license_expiry_date?: string;
  registration_number?: string;
  nic_code?: string;
  contact_email?: string;
  communication_email?: string;
  contact_person?: string;
  mobile_number?: string;
  created_at?: string;
  updated_at?: string;
}

export function CompaniesPage() {
  const { canWrite } = useAuth();
  const isWrite = canWrite('companies');

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>('company_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'updated_at' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    selectedCount,
    masterCheckboxRef,
  } = useBulkSelection(companies);

  const fields = useFormFields({
    short_code: '', company_name: '', occupier_name: '',
    factory_address: '', license_number: '', license_expiry_date: '', registration_number: '',
    nic_code: '', contact_email: '', communication_email: '', contact_person: '', mobile_number: '',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'records' | 'drafts'>('records');
  const [draftCount, setDraftCount] = useState<number>(() => draftManager.listDrafts('company').length);
  const [isResumed, setIsResumed] = useState(false);
  const handledResumeRef = useRef<string | null>(null);

  useEffect(() => {
    const update = () => setDraftCount(draftManager.listDrafts('company').length);
    update();
    const unsub = draftManager.subscribe(update);
    return unsub;
  }, []);

  const restoreCompanyDraft = useCallback((draft: UserDraft) => {
    const data = draft.form_data as Record<string, unknown>;
    setEditing(null);
    if (data && typeof data === 'object') {
      fields.patchValues(data as any);
    }
    setModalOpen(true);
  }, []);

  const handleResumeDraft = (draft: UserDraft) => {
    setIsResumed(true);
    restoreCompanyDraft(draft);
  };

  // Check URL search params for ?resumeDraft=...
  useEffect(() => {
    const resumeId = searchParams.get('resumeDraft');
    if (resumeId && handledResumeRef.current !== resumeId) {
      handledResumeRef.current = resumeId;
      const draft = draftManager.getDraft(resumeId);
      if (draft && draft.entity_type === 'company') {
        setIsResumed(true);
        restoreCompanyDraft(draft);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('resumeDraft');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, restoreCompanyDraft]);

  const isCompanyDirty = useMemo(() => {
    if (!modalOpen || editing) return false;
    return Boolean(
      fields.values.company_name ||
      fields.values.short_code ||
      fields.values.occupier_name ||
      fields.values.license_number ||
      fields.values.contact_email ||
      fields.values.communication_email
    );
  }, [modalOpen, editing, fields.values]);

  const draftTitle = useMemo(() => {
    return fields.values.company_name ? `Company: ${fields.values.company_name}` : 'New Company';
  }, [fields.values.company_name]);

  const { existingDraft, restoreDraft, discardDraft, clearDraft } = useDraftAutoSave({
    entityType: 'company',
    draftId: 'dft_company_new',
    enabled: modalOpen && !editing,
    isDirty: isCompanyDirty,
    isResumed,
    formData: fields.values,
    title: draftTitle,
    subtitle: fields.values.factory_address || undefined,
  });

  const load = () => {
    setLoading(true);
    api.companies.list({ search, page, limit: 20, sortBy, sortOrder })
      .then((r) => {
        setCompanies(r.data as Company[] ?? []);
        setTotalPages((r as unknown as Record<string, number>).totalPages ?? 1);
        setTotal((r as unknown as Record<string, number>).total ?? 0);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, sortBy, sortOrder]);

  useDebounce(search, () => { setPage(1); load(); });

  const [checkingShortCode, setCheckingShortCode] = useState(false);
  const [shortCodeStatus, setShortCodeStatus] = useState<{ available?: boolean; message?: string; suggested?: string } | null>(null);

  useDebounce(fields.values.short_code, async () => {
    const code = fields.values.short_code.trim();
    if (!code) {
      setShortCodeStatus(null);
      return;
    }
    setCheckingShortCode(true);
    try {
      const res = await api.companies.checkShortCode(code, fields.values.company_name, editing?.id);
      if (res.data) {
        setShortCodeStatus(res.data);
      }
    } catch {
      setShortCodeStatus(null);
    } finally {
      setCheckingShortCode(false);
    }
  }, 350);

  const openCreate = () => {
    setIsResumed(false);
    setActiveTab('records');
    setEditing(null);
    setShortCodeStatus(null);
    fields.reset({
      short_code: '', company_name: '', occupier_name: '',
      factory_address: '', license_number: '', license_expiry_date: '', registration_number: '',
      nic_code: '', contact_email: '', communication_email: '', contact_person: '', mobile_number: '',
    });
    setModalOpen(true);
  };

  useEffect(() => {
    if (editing) return;
    const name = fields.values.company_name.trim();
    if (!name) return;
    const candidates = generateSmartShortCodeCandidates(name);
    if (candidates.length > 0) fields.setField('short_code', candidates[0]);
  }, [fields.values.company_name, editing]);

  const handleSuggestShortCode = async () => {
    const name = fields.values.company_name.trim();
    if (!name) return;
    try {
      const res = await api.companies.suggestShortCode(name, editing?.id);
      if (res.data?.short_code) {
        fields.setField('short_code', res.data.short_code);
      }
    } catch {
      const candidates = generateSmartShortCodeCandidates(name);
      if (candidates.length > 0) fields.setField('short_code', candidates[0]);
    }
  };

  const openEdit = (c: Company) => {
    setIsResumed(false);
    setEditing(c);
    fields.patchValues({
      short_code: c.short_code ?? '',
      company_name: c.company_name ?? '',
      occupier_name: c.occupier_name ?? '',
      factory_address: c.factory_address ?? '',
      license_number: c.license_number ?? '',
      license_expiry_date: c.license_expiry_date ?? '',
      registration_number: c.registration_number ?? '',
      nic_code: c.nic_code ?? '',
      contact_email: c.contact_email ?? '',
      communication_email: c.communication_email ?? '',
      contact_person: c.contact_person ?? '',
      mobile_number: c.mobile_number ?? '',
    });
    setModalOpen(true);
  };

  const isCompanyFormValid = useMemo(() => {
    const v = fields.values;
    return (
      !!v.company_name.trim() &&
      !!v.short_code.trim() &&
      !!v.license_number.trim() &&
      !!v.registration_number.trim() &&
      validateMultipleEmails(v.contact_email) &&
      validateOptionalMultipleEmails(v.communication_email) &&
      (shortCodeStatus?.available !== false)
    );
  }, [fields.values, shortCodeStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing: string[] = [];
    if (!fields.values.company_name.trim()) missing.push('Company Name');
    if (!fields.values.short_code.trim()) missing.push('Short Code');
    if (!fields.values.license_number.trim()) missing.push('License Number');
    if (!fields.values.registration_number.trim()) missing.push('Registration Number');
    if (!fields.values.contact_email.trim()) missing.push('Contact Email');

    if (missing.length) {
      setError(`Please fill in all mandatory fields: ${missing.join(', ')}.`);
      return;
    }

    const emailVal = fields.values.contact_email.trim();
    if (!validateMultipleEmails(emailVal)) {
      setError('Please enter valid Contact Email address(es), separated by commas.');
      return;
    }

    const commsEmailVal = fields.values.communication_email.trim();
    if (commsEmailVal && !validateOptionalMultipleEmails(commsEmailVal)) {
      setError('Please enter valid Communication Email address(es), separated by commas.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...fields.values,
        contact_email: formatMultipleEmails(emailVal) || '',
        communication_email: formatMultipleEmails(commsEmailVal) || '',
      };
      if (editing) {
        await api.companies.update(editing.id, payload);
      } else {
        await api.companies.create(payload);
      }
      clearDraft();
      setModalOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.companies.delete(deleteId);
      setSuccess('Company moved to Trash.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await api.companies.bulkDelete(Array.from(selectedIds));
      setSuccess(res.data?.message || `Moved ${res.data?.count ?? selectedIds.size} companies to Trash.`);
      clearSelection();
      setBulkDeleteDialogOpen(false);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected companies');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* Action Toolbar */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Tab Pill Toggle */}
          <div className="flex items-center p-0.5 bg-gray-200/80 rounded-lg border border-gray-300/70">
            <button
              type="button"
              onClick={() => setActiveTab('records')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer ${
                activeTab === 'records'
                  ? 'bg-white text-gray-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Companies
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('drafts')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'drafts'
                  ? 'bg-white text-amber-900 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>Drafts</span>
              {draftCount > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-amber-200">
                  {draftCount}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'records' && (
            <button onClick={load} className="btn btn-secondary">Refresh</button>
          )}
        </div>
        {isWrite && (
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={openCreate} className="btn btn-primary">+ Add Company</button>
          </div>
        )}
      </div>

      {/* Search Bar */}
      {activeTab === 'records' && (
        <div className="shrink-0 bg-white p-3 rounded-xl border border-gray-200 shadow-sm flex items-center">
          <div className="relative w-full sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search companies…"
              className="input pl-9 text-sm w-full"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs font-semibold text-emerald-800 flex items-center justify-between shadow-xs">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-emerald-600 hover:text-emerald-900 ml-2 font-bold cursor-pointer">×</button>
        </div>
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Table */}
      {activeTab === 'drafts' ? (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <SectionDraftsView
            entityType="company"
            sectionTitle="Company"
            onResume={handleResumeDraft}
          />
        </div>
      ) : loading ? (
        <TableLoader />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      ref={masterCheckboxRef}
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      title="Select all"
                    />
                  </th>
                  <SortableHeader label="Company Name" field="company_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Short Code" field="short_code" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Occupier" field="occupier_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Contact Person" field="contact_person" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Mobile</th>
                  <th>Email</th>
                  <SortableHeader label="Created / Updated" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-gray-500 py-8">No companies found</td></tr>
                ) : companies.map((c) => (
                  <tr key={c.id} className={isSelected(c.id) ? 'bg-brand-50/50 hover:bg-brand-50/70' : undefined}>
                    <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected(c.id)}
                        onChange={() => {}}
                        onClick={(e) => toggleSelect(c.id, e.shiftKey)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => setViewingCompany(c)}
                        className="font-semibold text-brand-600 hover:text-brand-800 hover:underline text-left cursor-pointer block text-sm truncate"
                      >
                        {c.company_name}
                      </button>
                      {c.license_expiry_date && (
                        <span className="block text-xs text-gray-400 font-normal mt-0.5">
                          Lic Exp: {formatDateOnly(c.license_expiry_date)}
                        </span>
                      )}
                    </td>
                    <td>
                      <CompanyHoverBadge shortCode={c.short_code} company={c} />
                    </td>
                    <td className="text-gray-500 text-sm">{c.occupier_name ?? '—'}</td>
                    <td className="text-gray-500 text-sm">{c.contact_person ?? '—'}</td>
                    <td className="text-gray-500 text-sm whitespace-nowrap">{c.mobile_number ?? '—'}</td>
                    <td className="text-gray-500 text-sm">
                      <div className="space-y-0.5">
                        {c.contact_email ? (
                          c.contact_email.split(/[,;\s]+/).filter(Boolean).map((em, idx) => (
                            <div key={idx} className="truncate max-w-[220px]" title={em}>{em}</div>
                          ))
                        ) : (
                          '—'
                        )}
                      </div>
                      {c.communication_email && (
                        <div className="text-xs text-brand-600 font-mono mt-0.5 truncate max-w-[220px]" title={`Communication: ${c.communication_email}`}>
                          {c.communication_email}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <TimestampCell createdAt={c.created_at} updatedAt={c.updated_at} />
                    </td>
                    <td>
                      <div className="flex gap-1.5 items-center">
                        <button
                          onClick={() => setViewingCompany(c)}
                          title="View Company Details"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/80 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>View</span>
                        </button>
                        {isWrite && (
                          <>
                            <button
                              onClick={() => openEdit(c)}
                              title="Edit Company"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            <button
                              onClick={() => setDeleteId(c.id)}
                              title="Delete Company"
                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* View Company Details Modal */}
      <Modal open={!!viewingCompany} onClose={() => setViewingCompany(null)} title="Company Details" size="lg">
        {viewingCompany && (
          <div className="space-y-5">
            {/* Overview Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">{viewingCompany.company_name}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Short Code: <span className="font-mono font-semibold text-brand-700">{viewingCompany.short_code}</span>
                </p>
              </div>
              {viewingCompany.nic_code && (
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-200 text-slate-800">
                    NIC: {viewingCompany.nic_code}
                  </span>
                </div>
              )}
            </div>

            {/* Details Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {/* Contact Info */}
              <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-1.5">
                  Contact Information
                </h4>
                <p><span className="text-gray-500">Contact Person:</span> <strong className="text-gray-900">{viewingCompany.contact_person || '—'}</strong></p>
                <p><span className="text-gray-500">Mobile Number:</span> <strong className="text-gray-900 font-mono">{viewingCompany.mobile_number || '—'}</strong></p>
                <div>
                  <span className="text-gray-500 block mb-1">Contact Email(s):</span>
                  <div className="flex flex-wrap gap-1">
                    {viewingCompany.contact_email ? (
                      viewingCompany.contact_email.split(/[,;\s]+/).filter(Boolean).map((em, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-slate-100 text-slate-800 border border-slate-200">
                          {em}
                        </span>
                      ))
                    ) : (
                      <strong className="text-gray-900">—</strong>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">Communication Email:</span>
                  <div className="flex flex-wrap gap-1">
                    {viewingCompany.communication_email ? (
                      viewingCompany.communication_email.split(/[,;\s]+/).filter(Boolean).map((em, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-brand-50 text-brand-800 border border-brand-200">
                          {em}
                        </span>
                      ))
                    ) : (
                      <strong className="text-gray-900">—</strong>
                    )}
                  </div>
                </div>
              </div>

              {/* License & Registration */}
              <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm space-y-2">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-1.5">
                  Licensing & Registration
                </h4>
                <p><span className="text-gray-500">License Number:</span> <strong className="text-gray-900 font-mono">{viewingCompany.license_number || '—'}</strong></p>
                <p>
                  <span className="text-gray-500">License Expiry Date:</span>{' '}
                  <strong className="text-gray-900 font-mono">
                    {formatDateOnly(viewingCompany.license_expiry_date)}
                  </strong>
                </p>
                <p><span className="text-gray-500">Registration No:</span> <strong className="text-gray-900 font-mono">{viewingCompany.registration_number || '—'}</strong></p>
              </div>
            </div>

            {/* Occupier & Address */}
            <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm space-y-2 text-sm">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-1.5">
                Occupier & Factory Location
              </h4>
              <p><span className="text-gray-500">Occupier Name:</span> <strong className="text-gray-900">{viewingCompany.occupier_name || '—'}</strong></p>
              <div>
                <span className="text-gray-500 block mb-1">Factory Address:</span>
                <p className="text-gray-800 whitespace-pre-line bg-gray-50 p-2.5 rounded-lg border border-gray-100 text-xs">
                  {viewingCompany.factory_address || '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add / Edit Company Modal */}
      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setIsResumed(false); }}
        title={editing ? 'Edit Company' : 'Add New Company'}
        size="lg"
      >
        <DraftRecoveryBanner
          draft={existingDraft}
          onRestore={() => {
            const data = restoreDraft();
            if (data && existingDraft) {
              restoreCompanyDraft({ ...existingDraft, form_data: data });
            }
          }}
          onDiscard={discardDraft}
        />
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Company Name"
              required
            >
              <input className="input" {...fields.bind('company_name')} placeholder="Full registered company name" required />
            </Field>
            <Field
              label="Short Code"
              required
              hint={
                <button
                  type="button"
                  onClick={handleSuggestShortCode}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Suggest Code
                </button>
              }
            >
              <div className="space-y-1">
                <input
                  className="input font-mono uppercase"
                  {...fields.bind('short_code')}
                  placeholder="e.g. SCHOTT"
                  required
                />
                {checkingShortCode && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <span className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                    Checking availability…
                  </p>
                )}
                {!checkingShortCode && shortCodeStatus && fields.values.short_code.trim() && (
                  shortCodeStatus.available ? (
                    <p className="text-xs text-emerald-600 font-medium">
                      ✓ Short code '{fields.values.short_code.toUpperCase()}' is available
                    </p>
                  ) : (
                    <div className="text-xs text-red-600 space-y-0.5">
                      <p>✕ {shortCodeStatus.message}</p>
                      {shortCodeStatus.suggested && (
                        <p>
                          Suggested code:{' '}
                          <button
                            type="button"
                            onClick={() => fields.setField('short_code', shortCodeStatus.suggested!)}
                            className="text-brand-700 underline font-semibold"
                          >
                            Use '{shortCodeStatus.suggested}'
                          </button>
                        </p>
                      )}
                    </div>
                  )
                )}
              </div>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="NIC Code">
              <input className="input" {...fields.bind('nic_code')} />
            </Field>
            <Field label="Contact Person Name">
              <input className="input" {...fields.bind('contact_person')} placeholder="e.g. Mr. John Doe" />
            </Field>
            <Field label="Mobile Number">
              <input className="input" {...fields.bind('mobile_number')} placeholder="e.g. +91 9876543210" />
            </Field>
          </div>
          <Field label="Occupier Name">
            <input className="input" {...fields.bind('occupier_name')} placeholder="Occupier name" />
          </Field>
          <Field label="Factory Address">
            <textarea className="input" rows={3} {...fields.bind('factory_address')} placeholder="Factory location & address" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="License Number" required>
              <input className="input" {...fields.bind('license_number')} />
            </Field>
            <Field label="License Expiry Date">
              <input className="input" type="date" {...fields.bind('license_expiry_date')} />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Registration Number" required>
              <input className="input" {...fields.bind('registration_number')} />
            </Field>
            <Field label="Contact Email(s)" required hint={<span className="text-[11px] text-gray-400">Separate multiple with commas</span>}>
              <input className="input" type="text" {...fields.bind('contact_email')} placeholder="e.g. person1@company.com, person2@company.com" autoComplete="off" required />
            </Field>
          </div>
          <Field label="Communication Email" hint={<span className="text-[11px] text-gray-400">Optional, separate multiple with commas</span>}>
            <input className="input" type="text" {...fields.bind('communication_email')} placeholder="e.g. comms@company.com" autoComplete="off" />
          </Field>
          {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => { setModalOpen(false); setIsResumed(false); }} className="btn btn-secondary text-sm h-9 whitespace-nowrap shrink-0">Cancel</button>
            <button type="submit" disabled={saving || !isCompanyFormValid} className="btn btn-primary text-sm h-9 px-5 whitespace-nowrap shrink-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        title="Move Company to Trash"
        message="Are you sure you want to delete this company? It will be moved to Trash and can be restored within 30 days."
        confirmLabel="Move to Trash"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      {isWrite && (
        <BulkActionBar
          selectedCount={selectedCount}
          entityName="companies"
          onClearSelection={clearSelection}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          isDeleting={bulkDeleting}
        />
      )}

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title={`Move ${selectedCount} Companies to Trash`}
        message={`Are you sure you want to delete ${selectedCount} selected companies? They will be moved to Trash and can be restored within 30 days.`}
        confirmLabel={`Move ${selectedCount} to Trash`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

    </div>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
        {hint}
      </div>
      {children}
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
      <span>{message}</span>
      <button onClick={onDismiss} className="text-red-500 hover:text-red-700">✕</button>
    </div>
  );
}
